import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import Button from '../ui/Button';
import businessInfo from '../../utils/businessInfo';
import ROUTES from '../../utils/routes';
import { getPhoneHref, getPhoneDisplay, getEmailHref } from '../../utils/businessInfo';

/**
 * Mobile navigation drawer.
 *
 * Accessibility behaviours implemented here:
 *  - Escape closes the drawer
 *  - focus moves into the drawer on open and returns to the toggle on close
 *  - focus is trapped inside the drawer while it is open
 *  - body scroll is locked so the page behind cannot move
 *  - the drawer is marked aria-modal and labelled by its own heading
 */
const MobileNav = ({ open, onClose, items, toggleRef }) => {
  const { pathname } = useLocation();

  // Close the drawer whenever the route changes.
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Escape to close, and keep Tab focus circulating inside the drawer.
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        toggleRef?.current?.focus();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = document
        .getElementById('mobile-navigation')
        ?.querySelectorAll(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Move focus into the drawer for screen-reader and keyboard users.
    const focusTimer = window.setTimeout(() => {
      document.querySelector('.mobile-nav__close')?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [open, onClose, toggleRef]);

  if (!open) return null;

  const phoneHref = getPhoneHref();
  const phoneDisplay = getPhoneDisplay();
  const emailHref = getEmailHref();

  return (
    <>
      <div className="mobile-nav__scrim" onClick={onClose} aria-hidden="true" />

      <nav
        id="mobile-navigation"
        className="mobile-nav"
        aria-label="Mobile navigation"
        aria-modal="true"
        role="dialog"
      >
        <div className="mobile-nav__header">
          <span className="mobile-nav__heading" id="mobile-nav-heading">
            Menu
          </span>
          <button
            type="button"
            className="mobile-nav__close"
            onClick={() => {
              onClose();
              toggleRef?.current?.focus();
            }}
            aria-label="Close navigation menu"
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
          </button>
        </div>

        <ul className="mobile-nav__list">
          {items.map((item) => (
            <li key={item.to}>
              <Link className="mobile-nav__link" to={item.to} onClick={onClose}>
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="mobile-nav__actions">
          <Button to={ROUTES.contact} variant="primary" size="lg" fullWidth onClick={onClose}>
            Schedule a Consultation
          </Button>

          <div className="mobile-nav__contact">
            <p className="text-small text-muted mb-0">
              <strong>{businessInfo.name}</strong>
            </p>
            {phoneHref ? (
              <a href={phoneHref}>{phoneDisplay}</a>
            ) : (
              <span className="text-small text-muted">{phoneDisplay}</span>
            )}
            {emailHref ? (
              <a href={emailHref}>{businessInfo.email}</a>
            ) : (
              <span className="text-small text-muted">{businessInfo.email}</span>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default MobileNav;
